import { useState } from "react";

import { addDays, dayLabel, todayStr } from "../domain/date";
import { calibrateStride, curWeight, kmToSteps, walkMet } from "../domain/energy";
import { tmProtocol, tmTotalSecs, tmTotals } from "../domain/treadmill";
import { useEasyTreadmill, useKaya } from "../store/kayaStore";
import { ActionBar } from "./ActionBar";
import { Sheet } from "./Sheet";

/**
 * "Log from the console": a session run on the machine's own program, or without the player,
 * typed in from the display — or a correction to a day already logged. Kaya otherwise only knows
 * its own protocol's assumption about what was run. Moved out of the Treadmill page unchanged;
 * render it only while open.
 */
export function TreadmillLogSheet({ onClose }: { onClose: () => void }) {
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const checkBadges = useKaya((s) => s.checkBadges);

  const today = todayStr();
  const phases = tmProtocol(S, !!useEasyTreadmill());
  const est = tmTotals(S, phases, phases.length);
  const totalMin = Math.round(tmTotalSecs(phases) / 60);
  const [entryDate, setEntryDate] = useState(today);
  const [consoleKm, setConsoleKm] = useState("");
  const [consoleMins, setConsoleMins] = useState("");
  const entryLog = S.logs[entryDate]?.treadmill || null;

  const logPreset = () => {
    const km = Number(consoleKm);
    const mins = Number(consoleMins);
    const haveKm = Number.isFinite(km) && km > 0 && km < 50;
    const haveMins = Number.isFinite(mins) && mins > 0 && mins < 300;
    const target = entryDate;
    const existing = S.logs[target]?.treadmill || null;
    // Correcting a past day: keep the times it was actually walked at, so the measured window
    // still lines up with the phone's readings rather than jumping to now.
    const at = existing?.at || new Date(target + "T19:00:00").toISOString();

    // The console's own figures beat our protocol's assumption about what was run. Speed is
    // derived from them so the calorie cost reflects the actual pace rather than a nominal one.
    const realKm = haveKm ? km : est.km;
    const realMins = haveMins ? mins : Math.round(tmTotalSecs(phases) / 60);
    const speed = realMins > 0 ? realKm / (realMins / 60) : 0;
    const kcal = haveKm ? Math.round(walkMet(speed) * curWeight(S) * (realMins / 60)) : est.kcal;

    update((d) => {
      if (!d.logs[target]) d.logs[target] = { done: {}, treadmill: null };
      const prev = d.logs[target].treadmill;
      d.logs[target].treadmill = {
        protocol: prev?.protocol || "preset",
        startedAt: prev?.startedAt || new Date(Date.parse(at) - realMins * 60_000).toISOString(),
        at,
        kcal,
        km: realKm,
        steps: kmToSteps(S, realKm),
        kmFromConsole: haveKm,
        mins: realMins,
        // a correction keeps whatever the phone already measured for that window
        measuredSteps: prev?.measuredSteps,
      };

      // Entering a real distance for a session the phone already measured is exactly the pairing
      // stride calibration wants — and the sync pass will not revisit it, because it only looks
      // at sessions with no measurement yet. So do it here.
      const t = d.logs[target].treadmill;
      if (t && haveKm && typeof t.measuredSteps === "number") {
        const cal = calibrateStride(d, t.km, t.measuredSteps);
        if (cal) {
          d.profile.strideM = cal.strideM;
          d.profile.strideSamples = cal.strideSamples;
        }
      }
    });
    showToast(
      existing
        ? "Session updated"
        : haveKm
          ? "Logged from the console figures"
          : "Treadmill logged (estimated)"
    );
    checkBadges();
    onClose();
  };

  return (
    <Sheet
      title="Log from the console"
      sub="For a session you ran without the player, or to correct a day"
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          primary={{ label: entryLog ? "Update session" : "Log session", onClick: logPreset }}
          secondary={{ label: "Cancel", onClick: onClose }}
        />
      }
    >
      <div className="field">
        <label htmlFor="tl-date">Day</label>
        <input
          id="tl-date"
          type="date"
          value={entryDate}
          min={addDays(today, -13)}
          max={today}
          onChange={(e) => setEntryDate(e.target.value || today)}
        />
      </div>
      <p className="train-field-note">
        {entryLog
          ? `${dayLabel(entryDate)} already has a session${entryLog.km ? ` — ${entryLog.km.toFixed(2)} km` : ""}${
              entryLog.kmFromConsole ? " from the console." : ", estimated. Saving replaces it."
            }${
              typeof entryLog.measuredSteps === "number"
                ? ` Your phone counted ${entryLog.measuredSteps.toLocaleString()} steps during it, so a real distance also teaches your stride.`
                : ""
            }`
          : "Nothing logged for this day yet."}
      </p>

      <div className="train-fields">
        <div className="field">
          <label htmlFor="tl-km">Distance (km)</label>
          <input
            id="tl-km"
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder={entryLog?.km ? entryLog.km.toFixed(2) : est.km.toFixed(2)}
            value={consoleKm}
            onChange={(e) => setConsoleKm(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="tl-min">Time (min)</label>
          <input
            id="tl-min"
            type="number"
            inputMode="numeric"
            placeholder={String(entryLog?.mins || totalMin)}
            value={consoleMins}
            onChange={(e) => setConsoleMins(e.target.value)}
          />
        </div>
      </div>
      <p className="train-field-note">
        Entering the real distance is also how Kaya learns your stride — it cannot learn it from a
        distance it only guessed.
      </p>
    </Sheet>
  );
}
